import { SafeProvider, } from "@safe-global/protocol-kit";
import { Buffer } from "buffer";
import { PasskeyError } from "./types.js";
import { arrayBufferToBase64Url, base64ToArrayBuffer, arrayBufferToHex, parseDERPublicKey, } from "./encoding.js";
import * as cbor from "cbor";
/**
 * Format PublicKeyCredential for registration (client to server)
 * Ensures publicKey is always extracted (browser or CBOR fallback)
 */
export function encodeRegistrationResponse(credential) {
    let transports = undefined;
    if (typeof credential.response.getTransports === "function") {
        transports = credential.response.getTransports();
    }
    let responsePublicKeyAlgorithm = undefined;
    if (typeof credential.response.getPublicKeyAlgorithm === "function") {
        try {
            responsePublicKeyAlgorithm = credential.response.getPublicKeyAlgorithm();
        }
        catch (error) {
            console.warn("Browser extension getPublicKeyAlgorithm() error:", error);
        }
    }
    let responsePublicKey = undefined;
    if (typeof credential.response.getPublicKey === "function") {
        try {
            const _publicKey = credential.response.getPublicKey();
            if (_publicKey !== null) {
                responsePublicKey = arrayBufferToBase64Url(_publicKey);
            }
        }
        catch (error) {
            console.warn("Browser extension getPublicKey() error:", error);
        }
    }
    // Fallback: extract public key from attestationObject using CBOR
    if (!responsePublicKey) {
        try {
            const attestationObject = credential.response.attestationObject;
            if (attestationObject) {
                const attestationStruct = cbor.decodeFirstSync(attestationObject);
                const authData = attestationStruct.authData;
                // Parse authData to extract the credentialPublicKey (COSE key)
                // See WebAuthn spec for offsets
                // https://www.w3.org/TR/webauthn/#sctn-authenticator-data
                // authData is a buffer, credentialPublicKey starts at a fixed offset
                // Rely on a helper to extract the COSE key
                const coseKey = extractCosePublicKeyFromAuthData(authData);
                if (coseKey) {
                    responsePublicKey = arrayBufferToBase64Url(coseKey);
                }
            }
        }
        catch (error) {
            console.warn("CBOR public key extraction error:", error);
        }
    }
    let responseAuthenticatorData = undefined;
    if (typeof credential.response.getAuthenticatorData === "function") {
        try {
            responseAuthenticatorData = arrayBufferToBase64Url(credential.response.getAuthenticatorData());
        }
        catch (error) {
            console.warn("Browser extension getAuthenticatorData() error:", error);
        }
    }
    return {
        id: credential.id,
        rawId: arrayBufferToBase64Url(credential.rawId),
        response: {
            attestationObject: arrayBufferToBase64Url(credential.response.attestationObject),
            clientDataJSON: arrayBufferToBase64Url(credential.response.clientDataJSON),
            transports,
            publicKeyAlgorithm: responsePublicKeyAlgorithm,
            publicKey: responsePublicKey,
            authenticatorData: responseAuthenticatorData,
        },
        type: credential.type,
        clientExtensionResults: credential.getClientExtensionResults(),
        authenticatorAttachment: credential.authenticatorAttachment || null,
    };
}
// Helper to extract COSE public key from authData buffer
function extractCosePublicKeyFromAuthData(authData) {
    // WebAuthn spec: authData layout
    // https://www.w3.org/TR/webauthn/#sctn-authenticator-data
    // RP ID hash (32 bytes) + flags (1 byte) + signCount (4 bytes) = 37 bytes
    // If attestedCredentialData is present, next comes:
    // - aaguid (16 bytes)
    // - credentialIdLength (2 bytes)
    // - credentialId (credentialIdLength bytes)
    // - credentialPublicKey (CBOR encoded)
    let offset = 37;
    offset += 16; // aaguid
    const credIdLen = (authData[offset] << 8) + authData[offset + 1];
    offset += 2;
    offset += credIdLen;
    // The rest is the credentialPublicKey (COSE key)
    const coseKey = authData.slice(offset);
    return coseKey.buffer.slice(coseKey.byteOffset, coseKey.byteOffset + coseKey.byteLength);
}
/**
 * Format PublicKeyCredential for authentication (client to server)
 */
export function encodeAuthenticationResponse(credential) {
    return {
        id: credential.id,
        rawId: arrayBufferToBase64Url(credential.rawId),
        response: {
            clientDataJSON: arrayBufferToBase64Url(credential.response.clientDataJSON),
            authenticatorData: arrayBufferToBase64Url(credential.response
                .authenticatorData),
            signature: arrayBufferToBase64Url(credential.response.signature),
        },
        type: credential.type,
    };
}
/**
 * Decode challenge for WebAuthn operations (server to client)
 */
export function decodeChallenge(challenge) {
    return {
        ...challenge,
        challenge: base64ToArrayBuffer(challenge.challenge),
        excludeCredentials: challenge.excludeCredentials?.map((credential) => ({
            ...credential,
            id: base64ToArrayBuffer(credential.id),
        })),
        allowCredentials: challenge.allowCredentials?.map((credential) => ({
            ...credential,
            id: base64ToArrayBuffer(credential.id),
        })),
        user: challenge.user
            ? { ...challenge.user, id: base64ToArrayBuffer(challenge.user.id) }
            : undefined,
    };
}
/**
 * Universal public key parser - handles both string (base64) and ArrayBuffer inputs
 */
export async function parsePublicKey(publicKey) {
    try {
        // If it's a string (base64), use DER parsing
        if (typeof publicKey === "string") {
            return parseDERPublicKey(publicKey);
        }
        // If it's an ArrayBuffer, use Web Crypto API
        if (typeof crypto !== "undefined" && crypto.subtle) {
            const algorithm = {
                name: "ECDSA",
                namedCurve: "P-256",
                hash: { name: "SHA-256" },
            };
            const key = await crypto.subtle.importKey("spki", publicKey, algorithm, true, ["verify"]);
            const { x, y } = await crypto.subtle.exportKey("jwk", key);
            const isValidCoordinates = !!x && !!y;
            if (!isValidCoordinates) {
                throw new PasskeyError("Failed to generate passkey coordinates", "COORDINATE_GENERATION_FAILED");
            }
            return {
                x: "0x" + Buffer.from(x, "base64").toString("hex"),
                y: "0x" + Buffer.from(y, "base64").toString("hex"),
            };
        }
        throw new PasskeyError("Web Crypto API not available for ArrayBuffer parsing", "CRYPTO_API_UNAVAILABLE");
    }
    catch (error) {
        throw new PasskeyError(`Failed to parse public key: ${error instanceof Error ? error.message : "Unknown error"}`, "PUBLIC_KEY_PARSING_FAILED", error);
    }
}
/**
 * Get the Ethereum address from passkey public key or signer
 * Unified function to parse address from passkey
 */
export async function getPasskeyAddress(passkey, safeAddress, rpcUrl) {
    try {
        const safe = await SafeProvider.init({
            provider: rpcUrl || "https://mainnet.base.org",
            signer: passkey,
            safeAddress,
        });
        const signerAddress = (await safe.getExternalSigner())?.account.address;
        if (!signerAddress) {
            throw new Error("Passkey is in a wrong format. Could not get address for it.");
        }
        return signerAddress;
    }
    catch (error) {
        throw new PasskeyError(`Failed to get passkey address: ${error instanceof Error ? error.message : "Unknown error"}`, "ADDRESS_CALCULATION_FAILED", error);
    }
}
/**
 * Create passkey signer from passkey data (core logic)
 * This is the pure signer creation without persistence or verification
 */
export async function createPasskeySigner(passkey, getFn) {
    try {
        // Parse the public key to get coordinates
        const { x, y } = await parsePublicKey(passkey.publicKey);
        // Convert credential ID from base64url to hex
        const rawIdBuffer = base64ToArrayBuffer(passkey.credentialId);
        const rawIdHex = arrayBufferToHex(rawIdBuffer);
        const signer = {
            rawId: rawIdHex,
            coordinates: {
                x: x,
                y: y,
            },
            getFn,
        };
        return signer;
    }
    catch (error) {
        throw new PasskeyError(`Failed to create passkey signer: ${error instanceof Error ? error.message : "Unknown error"}`, "SIGNER_CREATION_FAILED", error);
    }
}
